import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { SOURCES } from '@/constants/testIds';
import {
  ChevronDown, ChevronRight, Chrome, Copy, ExternalLink,
  ShieldCheck, CheckCircle2, Circle, MinusCircle, Download,
  Info,
} from 'lucide-react';

// Backend origin — download link must go directly to the API host, without
// prepending another "/api" (backend already returns the fully-qualified
// "/api/extension/download" path).
const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;

/**
 * Sep 2026 §Platform/Capability doctrine — Connect page:
 *   1) Smart Scan Sources — collapsible summary card (unchanged).
 *   2) Guided Scan · Your Connected Accounts — organized by PLATFORM.
 *      One login unlocks every supported surface for that platform.
 *      Each platform expands to reveal per-capability status.
 *   3) Extension install-path: the Download button is ALWAYS available on
 *      every device/browser (per owner: download anywhere, install/pair
 *      only where the browser actually supports the extension). What we
 *      gate by browser support is Pair Extension + connection flow, not
 *      the download itself.
 */

// Detect an extension-capable browser environment (Chrome/Edge/Brave/Opera
// on desktop). Firefox, Safari, and any mobile UA cannot install/pair a
// Chromium unpacked extension. The ZIP download is NOT gated by this — it
// only gates the pairing / connection UI.
const detectExtensionSupport = () => {
  if (typeof navigator === 'undefined') return { supported: true, reason: null };
  const ua = navigator.userAgent || '';
  const uaData = navigator.userAgentData;

  // 1) Mobile — user-agent client hints (Chromium) is the strongest signal.
  if (uaData?.mobile) return { supported: false, reason: 'mobile' };

  // 2) Mobile — classic UA regex (covers Android Chrome, iOS Safari, iPad
  //    including iPadOS which advertises Mac UA but reports touch).
  if (/Android|iPhone|iPod|Mobile|IEMobile|Opera Mini|BlackBerry|webOS/i.test(ua)) {
    return { supported: false, reason: 'mobile' };
  }

  // 3) iPad running iPadOS reports desktop-Mac UA — catch via touch heuristic.
  if (/iPad/i.test(ua)) return { supported: false, reason: 'mobile' };
  const isAppleTouchDevice = ua.includes('Macintosh')
    && typeof navigator.maxTouchPoints === 'number'
    && navigator.maxTouchPoints > 1;
  if (isAppleTouchDevice) return { supported: false, reason: 'mobile' };

  // 4) Firefox — installs .xpi, not Chromium unpacked. Blocked.
  if (/Firefox\//i.test(ua)) return { supported: false, reason: 'firefox' };

  // 5) Safari desktop — cannot install Chromium extensions. Chromium
  //    browsers all include "Safari" in the UA, so exclude them first.
  if (/Safari\//i.test(ua) && !/Chrome\/|Chromium\/|Edg\/|OPR\/|Brave\//i.test(ua)) {
    return { supported: false, reason: 'safari' };
  }

  return { supported: true, reason: null };
};

const Connect = () => {
  const [smartOpen, setSmartOpen] = useState(false);
  const [smartSources, setSmartSources] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [extStatus, setExtStatus] = useState({ paired: false });
  const [expanded, setExpanded] = useState(new Set());
  const [pairing, setPairing] = useState(null);
  const [showPairPanel, setShowPairPanel] = useState(false);
  const [installInfo, setInstallInfo] = useState(null);
  const [showSteps, setShowSteps] = useState(false);
  const [browserSupport] = useState(() => detectExtensionSupport());

  useEffect(() => {
    // Fetch install info once — public endpoint, no auth needed but api
    // client sends token harmlessly.
    (async () => {
      try {
        const { data } = await api.get('/extension/install-info');
        setInstallInfo(data);
      } catch { /* silent */ }
    })();
  }, []);

  const load = async () => {
    try {
      const { data: pd } = await api.get('/extension/platforms');
      setPlatforms(pd.platforms || []);
      setExtStatus(pd.extension || { paired: false });
    } catch { /* ignore */ }
    try {
      const { data: sd } = await api.get('/sources');
      setSmartSources((sd.sources || []).filter((s) => s.connection_type === 'public'));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    // Auto-refresh so a platform silently moves Not Connected → Connected
    // once the extension reports an authenticated session for it.
    let interval;
    const start = () => { if (!interval) interval = setInterval(load, 12000); };
    const stop = () => { if (interval) { clearInterval(interval); interval = undefined; } };
    if (document.visibilityState === 'visible') start();
    const handler = () => {
      if (document.visibilityState === 'visible') { load(); start(); } else stop();
    };
    document.addEventListener('visibilitychange', handler);
    return () => { stop(); document.removeEventListener('visibilitychange', handler); };
  }, []);

  const toggleSmart = async (sk, enabled) => {
    await api.post(`/sources/${sk}/toggle`, { enabled });
    load();
  };

  const toggleCapability = async (platform_key, capability, enabled) => {
    try {
      await api.post(`/extension/platforms/${platform_key}/toggle-capability`, {
        capability, enabled,
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };

  const startPair = async () => {
    try {
      const { data } = await api.post('/extension/pair/start');
      setPairing(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start pairing');
    }
  };

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const openPlatformLogin = (login_url) => {
    if (!login_url) return;
    window.open(login_url, '_blank', 'noopener');
    toast.success('Opened login in a new tab. Sign in there; Zita will detect the session automatically.');
  };

  const activeSmartCount = smartSources.filter((s) => s.enabled && s.status === 'active').length;
  const connected = platforms.filter((p) => p.ui_status === 'connected');
  const reconnect = platforms.filter((p) => p.ui_status === 'reconnect_required');
  const notConnected = platforms.filter((p) => p.ui_status === 'not_connected');

  return (
    <div className="max-w-4xl mx-auto space-y-8 py-6 px-4 md:px-8">
      <header>
        <h1 className="font-serif text-4xl text-zita-espresso">Connect</h1>
        <p className="mt-2 text-zita-mocha max-w-2xl">
          Zita runs <strong>Smart Scans</strong> against public marketplaces and business
          directories, and <strong>Guided Scans</strong> against platforms that require your
          own signed-in browser session (via the Zita Guided Scan Extension). Zita cloud never
          sees your platform passwords or cookies.
        </p>
      </header>

      {/* Smart Scan collapsed summary */}
      <section className="zita-card p-0 overflow-hidden" data-testid="connect-smart-section">
        <button
          type="button"
          onClick={() => setSmartOpen(!smartOpen)}
          data-testid="connect-smart-toggle"
          className="w-full flex items-center gap-3 px-5 py-4 hover:bg-zita-sand/40 transition text-left"
        >
          {smartOpen
            ? <ChevronDown size={18} className="text-zita-mocha shrink-0" />
            : <ChevronRight size={18} className="text-zita-mocha shrink-0" />
          }
          <div className="flex-1 min-w-0">
            <div className="font-serif text-xl text-zita-espresso">Smart Scan Sources</div>
            <div className="text-sm text-zita-mocha mt-0.5 flex items-center gap-2">
              <CheckCircle2 size={13} className="text-emerald-600" />
              <span data-testid="connect-smart-summary">
                {activeSmartCount} active source{activeSmartCount !== 1 ? 's' : ''}
                {' · '}scanned directly, no login required
              </span>
            </div>
          </div>
          <span className="text-xs text-zita-mocha uppercase tracking-wider">
            {smartOpen ? 'Hide' : 'View sources'}
          </span>
        </button>
        {smartOpen && (
          <div
            data-testid="connect-smart-list"
            className="border-t border-zita-taupe/50 divide-y divide-zita-taupe/50"
          >
            {smartSources.map((s) => (
              <div
                key={s.source_key}
                className="flex items-center gap-3 px-5 py-3 hover:bg-zita-sand/30 transition"
                data-testid={`${SOURCES.card}-${s.source_key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zita-espresso truncate">{s.display_name}</div>
                  <div className="text-xs text-zita-mocha truncate">{s.description}</div>
                </div>
                <label className="text-xs text-zita-mocha flex items-center gap-1.5 shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!s.enabled}
                    onChange={(e) => toggleSmart(s.source_key, e.target.checked)}
                    data-testid={`${SOURCES.toggle}-${s.source_key}`}
                  />
                  {s.enabled ? 'On' : 'Off'}
                </label>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Guided Scan — Platform accounts */}
      <section className="space-y-4" data-testid="connect-guided-section">
        <div>
          <h2 className="font-serif text-2xl text-zita-espresso">Guided Scan · Your Connected Accounts</h2>
          <p className="text-sm text-zita-mocha mt-1 max-w-2xl">
            These platforms block cloud scraping. Zita's Guided Scan Extension uses your own
            signed-in browser session — nothing sensitive leaves your device. One login
            per platform unlocks every supported surface (Marketplace, Groups, Jobs, etc.).
          </p>
        </div>

        {/* Zita Guided Scan Extension — install + pair */}
        <ExtensionInstallCard
          extStatus={extStatus}
          browserSupport={browserSupport}
          installInfo={installInfo}
          showPairPanel={showPairPanel}
          setShowPairPanel={setShowPairPanel}
          showSteps={showSteps}
          setShowSteps={setShowSteps}
          pairing={pairing}
          onStartPair={startPair}
        />

        {/* Zita Viability Probe — Android-only technical probe (owner-scoped, Sep 5 2026) */}
        <ViabilityProbeCard />

        {connected.length > 0 && (
          <div className="space-y-2" data-testid="connect-connected-list">
            <div className="text-xs uppercase tracking-wider text-zita-mocha font-semibold">
              Connected accounts
            </div>
            {connected.map((p) => (
              <PlatformRow
                key={p.platform_key}
                p={p}
                variant="connected"
                expanded={expanded.has(p.platform_key)}
                onExpand={() => toggleExpand(p.platform_key)}
                onToggleCap={toggleCapability}
                onOpenPlatform={() => openPlatformLogin(p.login_url)}
                extPaired={extStatus.paired}
              />
            ))}
          </div>
        )}

        {reconnect.length > 0 && (
          <div className="space-y-2" data-testid="connect-reconnect-list">
            <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold">
              Reconnect required
            </div>
            {reconnect.map((p) => (
              <PlatformRow
                key={p.platform_key}
                p={p}
                variant="reconnect"
                expanded={expanded.has(p.platform_key)}
                onExpand={() => toggleExpand(p.platform_key)}
                onToggleCap={toggleCapability}
                onConnect={() => openPlatformLogin(p.login_url)}
                extPaired={extStatus.paired}
              />
            ))}
          </div>
        )}

        {notConnected.length > 0 && (
          <div className="space-y-2" data-testid="connect-notconnected-list">
            <div className="text-xs uppercase tracking-wider text-zita-mocha font-semibold">
              Not connected
            </div>
            {notConnected.map((p) => (
              <PlatformRow
                key={p.platform_key}
                p={p}
                variant="notconnected"
                expanded={expanded.has(p.platform_key)}
                onExpand={() => toggleExpand(p.platform_key)}
                onToggleCap={toggleCapability}
                onConnect={() => openPlatformLogin(p.login_url)}
                extPaired={extStatus.paired}
              />
            ))}
          </div>
        )}

        <div
          className="ari-chip inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold"
          data-testid="guided-scan-trust-badge"
        >
          <ShieldCheck size={12} />
          <span>
            Your accounts stay under your control. Zita never solves CAPTCHAs, enters MFA
            codes, or bypasses platform security — you always complete verification yourself.
          </span>
        </div>
      </section>
    </div>
  );
};

const PlatformRow = ({
  p, variant, expanded, onExpand,
  onConnect, onOpenPlatform, onToggleCap, extPaired,
}) => {
  const isConnected = variant === 'connected';
  const isReconnect = variant === 'reconnect';
  const buttonLabel = isReconnect ? 'Reconnect' : 'Connect';
  const implementedCount = p.capabilities.filter((c) => c.implemented).length;
  const totalCount = p.capabilities.length;

  return (
    <div
      data-testid={`connect-platform-${p.platform_key}`}
      className="zita-card p-0 overflow-hidden"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onExpand}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onExpand(); }}
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-zita-sand/30 transition"
      >
        {expanded
          ? <ChevronDown size={16} className="text-zita-mocha shrink-0" />
          : <ChevronRight size={16} className="text-zita-mocha shrink-0" />
        }
        <div className="flex-1 min-w-0">
          <div className="font-medium text-zita-espresso truncate">
            {isConnected && <CheckCircle2 size={13} className="inline mb-0.5 mr-1.5 text-emerald-600" />}
            {isReconnect && <Circle size={13} className="inline mb-0.5 mr-1.5 text-amber-600" />}
            {variant === 'notconnected' && <Circle size={13} className="inline mb-0.5 mr-1.5 text-zita-mocha" />}
            {p.display_name}
            {isReconnect && <span className="text-amber-700 text-xs ml-1">— session expired</span>}
          </div>
          <div className="text-[11px] text-zita-mocha mt-0.5">
            {implementedCount} of {totalCount} surface{totalCount !== 1 ? 's' : ''} supported
          </div>
        </div>
        {!isConnected && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onConnect?.(); }}
            data-testid={`connect-connect-${p.platform_key}`}
            disabled={!extPaired}
            title={extPaired ? '' : 'Install the Zita Guided Scan Extension first'}
            className={`text-xs shrink-0 disabled:opacity-50 inline-flex items-center gap-1 ${
              isReconnect ? 'zita-btn-primary' : 'zita-btn-secondary'
            }`}
          >
            {buttonLabel} <ExternalLink size={11} />
          </button>
        )}
      </div>
      {expanded && (
        <div className="px-5 py-3 border-t border-zita-taupe/50 text-sm text-zita-mocha space-y-3 bg-zita-sand/20">
          <p>{p.description}</p>
          {isReconnect && p.session_reason && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Reason: {p.session_reason}
            </p>
          )}
          <div>
            <div className="text-xs uppercase tracking-wider text-zita-mocha font-semibold mb-2">
              Surfaces Zita can scan
            </div>
            <ul className="space-y-1.5" data-testid={`platform-caps-${p.platform_key}`}>
              {p.capabilities.map((c) => (
                <li
                  key={c.key}
                  className="flex items-center gap-2 text-sm"
                  data-testid={`platform-cap-${p.platform_key}-${c.key}`}
                >
                  {c.implemented
                    ? <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                    : <MinusCircle size={13} className="text-zita-muted shrink-0" />
                  }
                  <span className={`flex-1 ${c.implemented ? 'text-zita-espresso' : 'text-zita-muted'}`}>
                    {c.name}
                    {!c.implemented && <span className="text-[11px] text-zita-muted"> — not yet</span>}
                  </span>
                  {c.implemented && isConnected && c.source_key && (
                    <label className="text-[11px] text-zita-mocha flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!c.enabled}
                        onChange={(e) => onToggleCap(p.platform_key, c.key, e.target.checked)}
                        data-testid={`platform-cap-toggle-${p.platform_key}-${c.key}`}
                      />
                      {c.enabled ? 'On' : 'Off'}
                    </label>
                  )}
                </li>
              ))}
            </ul>
          </div>
          {isConnected && onOpenPlatform && (
            <button
              type="button"
              onClick={onOpenPlatform}
              data-testid={`connect-open-${p.platform_key}`}
              className="text-xs text-zita-mocha hover:text-zita-espresso hover:underline inline-flex items-center gap-1"
            >
              Open {p.display_name} <ExternalLink size={10} />
            </button>
          )}
          {p.last_success_at && (
            <div className="text-[11px] text-zita-muted">
              Last successful session: {new Date(p.last_success_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Connect;


// ============================================================================
// Sep 2026 §Guided Scan Install Path — install + pair the extension
// ============================================================================
// Owner rule (Sep 2026):
//   Download anywhere. Install/pair only where the browser actually
//   supports Chromium unpacked extensions.
//
// Every device/browser sees:
//   • The Download Guided Scan Extension button
//   • Install instructions
//   • Clear guidance on which browsers can actually run the extension
//
// Supported desktop Chromium (Chrome / Edge / Brave / Opera) ALSO sees:
//   • The Pair Extension flow (generate a pairing code)
//
// Unsupported browsers (mobile, iPad, Firefox, Safari desktop) see a
// friendly note explaining that pairing requires a desktop Chromium
// browser — but the download itself remains available.

const UNSUPPORTED_MESSAGE = {
  mobile: 'This device can\'t run the extension, but you can still download the package here and install it later on a desktop Chromium browser (Chrome, Edge, Brave, or Opera).',
  firefox: 'Firefox can\'t run this Chromium extension. Download it here if you like, then open Zita in Chrome, Edge, Brave, or Opera on a desktop to install and pair it.',
  safari: 'Safari can\'t run Chromium extensions. Download it here if you like, then open Zita in Chrome, Edge, Brave, or Opera on a desktop to install and pair it.',
};

const ExtensionInstallCard = ({
  extStatus, browserSupport, installInfo,
  showPairPanel, setShowPairPanel,
  showSteps, setShowSteps,
  pairing, onStartPair,
}) => {
  // Already paired — compact "Connected" state (only reachable on supported
  // desktop Chromium, since pairing requires it — but we still render it
  // if the backend reports paired for any reason).
  if (extStatus.paired) {
    return (
      <div
        className="zita-card p-4 flex flex-wrap items-center gap-3"
        data-testid="connect-extension-row"
      >
        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 shrink-0">
          <Chrome size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-zita-espresso">Zita Guided Scan Extension</div>
          <div className="text-xs text-emerald-700 mt-0.5" data-testid="connect-extension-status">
            <CheckCircle2 size={11} className="inline mb-0.5 mr-1" />
            Connected · v{extStatus.installed_version || installInfo?.version || '1.0.0'}
          </div>
        </div>
      </div>
    );
  }

  const isSupported = browserSupport.supported;
  const downloadHref = `${BACKEND_ORIGIN}${installInfo?.download_url || '/api/extension/download'}`;

  return (
    <div
      className="zita-card p-5 space-y-4"
      data-testid="connect-extension-install"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-zita-sand flex items-center justify-center text-zita-cocoa shrink-0">
          <Chrome size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-zita-espresso">
            {installInfo?.product_name || 'Zita Guided Scan Extension'}
          </div>
          <div className="text-xs text-zita-mocha mt-0.5">
            <span className="uppercase tracking-wider font-semibold">Required for Guided Scan</span>
            {installInfo?.version ? ` · v${installInfo.version}` : ''}
          </div>
          <p className="text-sm text-zita-mocha mt-2">
            Extends your browser so Zita can search your signed-in Facebook, Nextdoor, and
            other supported accounts. Zita never sees your platform passwords or session cookies.
          </p>
          {!isSupported && (
            <p
              className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-2 mt-3"
              data-testid="connect-extension-unsupported-note"
            >
              {UNSUPPORTED_MESSAGE[browserSupport.reason] || UNSUPPORTED_MESSAGE.mobile}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-zita-taupe/50">
        {/* Download — ALWAYS available on every device/browser */}
        <a
          href={downloadHref}
          download
          data-testid="connect-install-extension"
          className="zita-btn-primary text-sm inline-flex items-center gap-1.5"
        >
          <Download size={14} /> Download Guided Scan Extension
        </a>
        <button
          type="button"
          onClick={() => setShowSteps(!showSteps)}
          data-testid="connect-install-steps-toggle"
          className="text-xs text-zita-mocha hover:text-zita-espresso hover:underline inline-flex items-center gap-1"
        >
          <Info size={12} />
          {showSteps ? 'Hide install steps' : 'How to install'}
        </button>
        {/* Pair — only offered where the extension can actually run */}
        {isSupported && (
          <button
            type="button"
            onClick={() => setShowPairPanel(true)}
            data-testid="connect-already-installed"
            className="ml-auto text-xs text-zita-mocha hover:text-zita-espresso hover:underline"
          >
            Already installed? Pair Extension →
          </button>
        )}
      </div>

      {showSteps && installInfo?.install_steps && (
        <ol
          data-testid="connect-install-steps"
          className="list-decimal list-inside space-y-1.5 text-sm text-zita-mocha bg-zita-sand/40 rounded-lg p-4"
        >
          {installInfo.install_steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      )}

      {isSupported && showPairPanel && !pairing && (
        <div className="rounded-lg border border-zita-taupe/60 bg-white p-4 space-y-2">
          <div className="text-sm text-zita-espresso font-medium">
            Ready to pair the extension?
          </div>
          <div className="text-xs text-zita-mocha">
            Click below to generate a one-time code. Open the Zita icon in your browser
            toolbar and paste it in.
          </div>
          <button
            type="button"
            onClick={onStartPair}
            data-testid={SOURCES.extensionPair}
            className="zita-btn-primary text-sm"
          >
            Generate pairing code
          </button>
        </div>
      )}

      {isSupported && pairing && (
        <div
          className="rounded-xl border border-ari/40 bg-ari/5 p-4 space-y-2"
          data-testid="connect-pairing-code"
        >
          <div className="text-xs uppercase tracking-wider ari-accent">Pairing code</div>
          <div className="flex items-center gap-3">
            <div
              data-testid={SOURCES.pairingCode}
              className="font-mono text-2xl tracking-widest text-zita-espresso select-all"
            >
              {pairing.code}
            </div>
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(pairing.code); toast.success('Copied'); }}
              className="p-2 rounded-full text-zita-mocha hover:bg-zita-sand"
              aria-label="Copy code"
            >
              <Copy size={14} />
            </button>
          </div>
          <div className="text-xs text-zita-mocha">
            Click the Zita icon in your browser toolbar and paste this code. Expires in 15 minutes.
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Sep 5, 2026 §Zita Viability Probe (Android APK)
// ============================================================================
// Owner-scoped, one-question technical probe. Answers:
//   "Can a stock Android WebView, without spoofing or credential
//    interception, obtain a usable authenticated Facebook session that
//    a future Guided Scan Android runtime could leverage?"
//
// The probe app DOES NOT scrape, ingest to Zita, or exfiltrate anything.
// Its only outputs are a plaintext report on-device that the owner
// copies + sends back manually.
//
// The download link is universal (per doctrine §Download Anywhere): any
// device can save the .apk, and the owner can transfer it to their
// Android phone. Installation is Android-only — noted in the copy.

const ViabilityProbeCard = () => {
  const [info, setInfo] = useState(null);
  const [showSteps, setShowSteps] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/extension/viability-probe/info');
        setInfo(data);
      } catch { /* silent */ }
    })();
  }, []);

  if (!info) return null;
  const href = `${BACKEND_ORIGIN}${info.download_url}`;
  const sizeMB = (info.size_bytes / (1024 * 1024)).toFixed(1);

  return (
    <div
      className="zita-card p-5 space-y-4 border-dashed"
      data-testid="connect-viability-probe"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 shrink-0">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-zita-espresso">
            {info.product_name}
          </div>
          <div className="text-xs text-zita-mocha mt-0.5">
            <span className="uppercase tracking-wider font-semibold">Android probe · owner-scoped</span>
            {info.version ? ` · v${info.version}` : ''}
            {' · '}~{sizeMB} MB
          </div>
          <p className="text-sm text-zita-mocha mt-2">
            {info.purpose}
          </p>
          <p
            className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-2 mt-3"
            data-testid="connect-viability-probe-scope"
          >
            <strong>v1.5 — layout restored to the familiar v1.2 shape.</strong> Owner
            reported Facebook is now serving a "Confirmation failed / try logging in from
            a device you have used before" hard-block screen — that's Facebook's
            server-side decision after repeated uninstall/reinstall cycles wiped cookies.
            No code fix will bypass an FB hard-block. What v1.5 does: (1) rolls back the
            v1.3 UI redesign to the 3+3+2 button layout you were familiar with,
            (2) keeps silent improvements — session persistence, hydration-aware extract
            retry, wide-net selectors, (3) detects FB's hard-block screen and surfaces a
            plain-language dialog explaining recovery. <strong>Install over existing —
            do NOT uninstall first — cookies survive that way.</strong> If the block
            persists, wait 24–48 hours or try from a second Android device.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-zita-taupe/50">
        <a
          href={href}
          download
          data-testid="connect-viability-probe-download"
          className="zita-btn-primary text-sm inline-flex items-center gap-1.5"
        >
          <Download size={14} /> Download Viability Probe (.apk)
        </a>
        <button
          type="button"
          onClick={() => setShowSteps(!showSteps)}
          data-testid="connect-viability-probe-steps-toggle"
          className="text-xs text-zita-mocha hover:text-zita-espresso hover:underline inline-flex items-center gap-1"
        >
          <Info size={12} />
          {showSteps ? 'Hide install steps' : 'How to install (Android)'}
        </button>
      </div>

      {showSteps && Array.isArray(info.install_steps) && (
        <ol
          data-testid="connect-viability-probe-steps"
          className="list-decimal list-inside space-y-1.5 text-sm text-zita-mocha bg-zita-sand/40 rounded-lg p-4"
        >
          {info.install_steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      )}
    </div>
  );
};

